import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../lib/queryClient";

const API = process.env.REACT_APP_API_URL || "";
const authHeader = () => {
  const token = localStorage.getItem("access");
  return token ? { Authorization: `JWT ${token}` } : {};
};

/**
 * Query hook for verification dashboard overview metrics
 */
export const useVerificationStatsQuery = () => {
  return useQuery({
    queryKey: [...queryKeys.verification.all, "stats"],
    queryFn: async ({ signal }) => {
      const res = await fetch(`${API}/verification/dashboard-stats/`, {
        headers: authHeader() as any,
        signal,
      });
      if (!res.ok) throw new Error("Failed to load verification stats");
      return res.json();
    },
  });
};

/**
 * Query hook for verification wallet & transaction history
 */
export const useVerificationWalletQuery = () => {
  return useQuery({
    queryKey: queryKeys.verification.wallet(),
    queryFn: async ({ signal }) => {
      const [wRes, tRes] = await Promise.all([
        fetch(`${API}/wallets/my-wallet/`, {
          headers: authHeader() as any,
          signal,
        }),
        fetch(`${API}/wallets/transactions/`, {
          headers: authHeader() as any,
          signal,
        }),
      ]);
      const wallet = wRes.ok ? await wRes.json() : null;
      const tData = tRes.ok ? await tRes.json() : [];
      const transactions = Array.isArray(tData) ? tData : tData.results ?? [];
      return { wallet, transactions };
    },
  });
};

/**
 * Query hook for verification services catalogue
 */
export const useVerificationServicesQuery = () => {
  return useQuery({
    queryKey: queryKeys.verification.services(),
    queryFn: async ({ signal }) => {
      const res = await fetch(`${API}/verification/list/`, {
        headers: authHeader() as any,
        signal,
      });
      if (!res.ok) throw new Error("Failed to load services");
      return res.json();
    },
  });
};

/**
 * Query hook for verification audit logs
 */
export const useVerificationLogsQuery = (page: number = 1) => {
  return useQuery({
    queryKey: [...queryKeys.verification.logs(), page],
    queryFn: async ({ signal }) => {
      const res = await fetch(`${API}/verification/logs/?page=${page}`, {
        headers: authHeader() as any,
        signal,
      });
      if (!res.ok) throw new Error("Failed to load logs");
      return res.json();
    },
  });
};

/**
 * Query hook for staff wallets (SuperAdmin view)
 */
export const useStaffWalletsQuery = (enabled: boolean = true) => {
  return useQuery({
    queryKey: queryKeys.verification.staffWallets(),
    queryFn: async ({ signal }) => {
      const res = await fetch(`${API}/wallets/staff-wallets/`, {
        headers: authHeader() as any,
        signal,
      });
      if (!res.ok) throw new Error("Failed to load staff wallets");
      return res.json();
    },
    enabled,
  });
};

/**
 * Mutation hook for allocating funds to a staff wallet or topping up
 */
export const useAllocateFundsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      target_user_id,
      amount,
      memo,
    }: {
      target_user_id?: number | string;
      amount: number;
      memo?: string;
    }) => {
      const res = await fetch(`${API}/wallets/top-up/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(authHeader() as any),
        },
        body: JSON.stringify({ target_user_id, amount, memo }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to allocate funds");
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.verification.wallet() });
      queryClient.invalidateQueries({ queryKey: queryKeys.verification.staffWallets() });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.verification.all, "stats"] });
    },
  });
};

/**
 * Mutation hook for staff requesting wallet allocation from SuperAdmin
 */
export const useRequestAllocationMutation = () => {
  return useMutation({
    mutationFn: async ({
      amount,
      reason,
    }: {
      amount: number;
      reason?: string;
    }) => {
      const res = await fetch(`${API}/wallets/request-allocation/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(authHeader() as any),
        },
        body: JSON.stringify({ amount, reason }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to submit allocation request");
      return data;
    },
  });
};
